<template lang="pug">
AppDialog.archive-organization-dialog(
  append-to-body
  show
  :title="dialogTitle"
  @close="close"
)
  p.alert.alert-info.d-flex.align-items-baseline
    i.hi.hi-info.mr-10(v-if="action === 'archive'")
    |
    | {{ t.dialogWarning }}

  p.alert.alert-info(v-if="appSumo")
    | Since you are on an AppSumo plan this will also archive the organization in Hubstaff Tasks.

  .form-group
    label.control-label Cancellation reason*
    AppSelect(
      v-model="primaryCategory"
      :options="primaryCategories"
      placeholder="Select a reason"
    )

  .form-group(v-if="secondaryCategories")
    label.control-label Please specify*
    AppSelect(
      v-model="secondaryCategory"
      :options="secondaryCategories"
      placeholder="Select an option that applies"
    )

  .form-group
    label.control-label Details
    textarea.form-control(
      v-model="details"
      maxlength="2000"
      rows="4"
      placeholder="Please tell us why you are leaving. We appreciate your feedback."
    )

  template(#footer)
    AppButton(@click="onCancel") {{ t.dialogSkip }}
    AppButton(
      v-tooltip="archiveDisabled"
      type="danger"
      :title="t.dialogDisabledTooltip"
      :disabled="archiveDisabled"
      @click="submit"
    )
      | {{ dialogLabel }}
</template>

<script>
import { AnalyticsResource, OrganizationsResource } from '~/resources';
import { shuffleReasons } from '~/utils/cancellation_reasons';

// Defined in `_frontend_runtime_vars.html.slim`
const CANCELLATION_CATEGORIES = window.CANCELLATION_CATEGORIES;
const TEXT = {
  archive: {
    dialogTitle: 'Archive confirmation',
    dialogAction: 'archive your organization',
    dialogWarning: 'Your subscription will be cancelled, and your team will no longer be able to track time.',
    dialogLabel: 'Archive',
    dialogLabelSubmitting: 'Archiving...',
    dialogSkip: 'Cancel',
    dialogDisabledTooltip: 'Please fill out all required fields to finalize archiving your organization',
  },
  cancel: {
    dialogTitle: 'Cancel subscription confirmation',
    dialogAction: 'cancel your subscription',
    dialogWarning: 'Your subscription will be cancelled at the end of current billing cycle.',
    dialogLabel: 'Cancel subscription',
    dialogLabelSubmitting: 'Cancelling subscription...',
    dialogSkip: 'Keep subscription',
    dialogDisabledTooltip: 'Please fill out all required fields to finalize canceling your subscription',
  },
};

export default {
  name: 'ArchiveOrganizationDialog',
  props: {
    organizationId: Number,
    appSumo: { type: Boolean, default: false },
    action: { type: String, default: 'archive' },
  },
  data() {
    return {
      primaryCategory: null,
      secondaryCategory: null,
      details: null,
      submitting: false,
    };
  },
  computed: {
    finalCategory() {
      if (this.secondaryCategories) {
        return this.secondaryCategory;
      }

      return this.primaryCategory;
    },
    archiveDisabled() {
      return this.submitting || !this.finalCategory;
    },
    primaryCategories() {
      return shuffleReasons(CANCELLATION_CATEGORIES.primary);
    },
    secondaryCategories() {
      if (this.primaryCategory in CANCELLATION_CATEGORIES.secondary) {
        const categories = CANCELLATION_CATEGORIES.secondary[this.primaryCategory].map((e) => {
          if (e instanceof Array) {
            return { text: e[0], id: e[1] };
          }

          return { text: e, id: e };
        });

        return shuffleReasons(categories, (category) => category.text);
      }

      return false;
    },
    dialogLabel() {
      return this.submitting ? this.t.dialogLabelSubmitting : this.t.dialogLabel;
    },
    dialogTitle() {
      return `Are you sure you'd like to ${this.t.dialogAction}?`;
    },
    t() {
      return TEXT[this.action];
    },
  },
  watch: {
    primaryCategory() {
      this.secondaryCategory = null;
    },
  },
  async mounted() {
    const event = this.action === 'archive' ? 'Archive organization clicked' : 'Cancel subscription clicked';
    let userId = null;

    try {
      userId = JSON.parse(document.body.dataset.currentUserDetails || '{}').id;
    } catch (e) {
      console.error('Failed to parse user ID from dataset:', e);
    }

    window.analytics?.track(event, { organization_id: this.organizationId, user_id: userId });
  },
  methods: {
    close() {
      this.$emit('close');
    },
    onCancel() {
      this.$emit('onCancel');
    },
    handleAction(data) {
      if (this.action === 'cancel') {
        return OrganizationsResource.cancelSubscription({ id: this.organizationId }, data);
      }

      return OrganizationsResource.archive({ id: this.organizationId }, data);
    },
    submit() {
      this.submitting = true;
      const data = {
        category: this.finalCategory,
        details: this.details,
      };

      this.handleAction(data)
        .then(async () => {
          const eventName = this.action === 'archive' ? 'Archive organization confirmed' : 'Cancel subscription confirmed';

          await AnalyticsResource.create(null, {
            event_name: eventName,
            event_properties: {
              organization_id: this.organizationId,
              action: this.action,
            },
          }).catch((error) => console.error('Failed to track archive/cancel confirmed:', error));
          document.location = OrganizationsResource.indexLink();
        })
        .catch((xhr) => {
          const { responseJSON: { error: errorMsg } = {}, statusText } = xhr;

          this.$appMessage(`Error occurred: ${errorMsg || statusText}`);
          this.submitting = false;
        });
    },
  },
};
</script>
